import { useMemo, useState, useCallback } from 'react'
import {
  Button,
  Checkbox,
  Group,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  Tooltip,
  UnstyledButton,
  ActionIcon,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import {
  IconChevronDown,
  IconChevronRight,
  IconCopy,
  IconClipboard,
  IconInfoCircle,
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import clsx from 'clsx'
import {
  useLoxoKpis,
  useLoxoScoringConfig,
  useUpdateLoxoScoringConfig,
} from './api'
import {
  simulateScoring,
  type ScoringOverrides,
  type KpiDetail,
} from './scoring'
import {
  buildTargetResolver,
  defaultDynamicParams,
  type TargetMode,
  type DynamicTargetParams,
} from './dynamicTargets'
import classes from './LoxoKpis.module.css'

export default function LoxoScoringSimulation({
  dateRange,
}: {
  dateRange: [Date | null, Date | null]
}) {
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const start = dayjs(dateRange[0] ?? '2025-01-01')
    .startOf('month')
    .format('YYYY-MM-DD')
  const end = dayjs(dateRange[1] ?? '2026-01-01')
    .startOf('month')
    .format('YYYY-MM-DD')

  const { data: loxoData } = useLoxoKpis({ start, end })
  const { data: configs } = useLoxoScoringConfig()

  const [overrides, setOverrides] = useState<ScoringOverrides>({})
  const [disabledKpis, setDisabledKpis] = useState<Set<string>>(new Set())
  const [configOpen, setConfigOpen] = useState(false)
  const [seasonalityOpen, setSeasonalityOpen] = useState(false)
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set())
  const [viewMode, setViewMode] = useState<
    'score' | 'rating' | 'actual' | 'target' | 'percent'
  >('score')
  const [selectedKpi, setSelectedKpi] = useState<string>('all')
  const saveMutation = useUpdateLoxoScoringConfig()

  const [copiedConfig, setCopiedConfig] = useState<{
    month_target: number | null
    month_percent_values: number[]
    month_score_values: number[]
  } | null>(null)

  const [targetMode, setTargetMode] = useState<TargetMode>('static')
  const [dynamicParams, setDynamicParams] =
    useState<DynamicTargetParams>(defaultDynamicParams)

  const months = useMemo(() => {
    const result: string[] = []
    let cursor = dayjs(start)
    const endDate = dayjs(end)
    while (cursor.isBefore(endDate)) {
      result.push(cursor.format('YYYY-MM-DD'))
      cursor = cursor.add(1, 'month')
    }
    return result
  }, [start, end])

  const kpiOptions = useMemo(
    () => [
      { value: 'all', label: 'All KPIs' },
      ...configs
        .filter((c) => !disabledKpis.has(c.key))
        .map((c) => ({ value: c.key, label: c.name })),
    ],
    [configs, disabledKpis]
  )

  const viewModeOptions = useMemo(() => {
    const base = [
      { value: 'score', label: 'Score' },
      { value: 'rating', label: 'Rating' },
    ]
    if (selectedKpi !== 'all') {
      base.push(
        { value: 'actual', label: 'Actual' },
        { value: 'target', label: 'Target' },
        { value: 'percent', label: 'Percent' }
      )
    }
    return base
  }, [selectedKpi])

  const targetResolver = useMemo(
    () =>
      buildTargetResolver(targetMode, loxoData, configs, months, dynamicParams),
    [targetMode, loxoData, configs, months, dynamicParams]
  )

  const results = useMemo(
    () =>
      simulateScoring(
        loxoData,
        configs,
        overrides,
        months,
        disabledKpis,
        targetResolver
      ),
    [loxoData, configs, overrides, months, disabledKpis, targetResolver]
  )

  // Per-user, per-month activity and max scores.
  // A KPI-month is active if actual > 0. A month is active if any KPI is active.
  // Max only counts active KPIs so ratings reflect achievable scores.
  const userMaxScores = useMemo(() => {
    // Pre-compute max score per KPI key
    const kpiMax = new Map<string, number>()
    for (const config of configs) {
      if (disabledKpis.has(config.key)) continue
      const ov = overrides[config.key]
      const scoreValues = ov?.month_score_values ?? config.month_score_values
      if (scoreValues.length > 0) {
        kpiMax.set(config.key, scoreValues[scoreValues.length - 1])
      }
    }

    const map = new Map<
      number,
      {
        monthMax: Record<string, number>
        totalMax: number
        monthActive: Record<string, boolean>
      }
    >()

    for (const user of results) {
      const info = {
        monthMax: {} as Record<string, number>,
        totalMax: 0,
        monthActive: {} as Record<string, boolean>,
      }

      for (const m of months) {
        const details = user.kpiDetails[m] ?? []
        let mMax = 0
        let anyActive = false

        if (selectedKpi !== 'all') {
          const detail = details.find((d) => d.key === selectedKpi)
          if (detail && detail.actual > 0) {
            anyActive = true
            mMax = kpiMax.get(selectedKpi) ?? 0
          }
        } else {
          for (const detail of details) {
            if (detail.actual > 0) {
              anyActive = true
              mMax += kpiMax.get(detail.key) ?? 0
            }
          }
        }

        info.monthActive[m] = anyActive
        info.monthMax[m] = mMax
        if (anyActive) info.totalMax += mMax
      }

      map.set(user.user_id, info)
    }

    return map
  }, [results, months, configs, overrides, disabledKpis, selectedKpi])

  const totals = useMemo(() => {
    const monthTotals: Record<string, number> = {}
    let grandTotal = 0
    for (const r of results) {
      if (selectedKpi === 'all') {
        grandTotal += r.totalPoints
        for (const m of months) {
          monthTotals[m] = (monthTotals[m] ?? 0) + (r.monthPoints[m] ?? 0)
        }
      } else {
        for (const m of months) {
          const detail = (r.kpiDetails[m] ?? []).find(
            (d) => d.key === selectedKpi
          )
          const score = detail?.score ?? 0
          monthTotals[m] = (monthTotals[m] ?? 0) + score
          grandTotal += score
        }
      }
    }
    return { monthTotals, grandTotal }
  }, [results, months, selectedKpi])

  type UserAgg = {
    monthActual: Record<string, number>
    monthTarget: Record<string, number>
    monthPercent: Record<string, number | null>
    totalActual: number
    totalTarget: number
  }

  const userAggregates = useMemo(() => {
    const map = new Map<number, UserAgg>()
    for (const user of results) {
      const agg: UserAgg = {
        monthActual: {},
        monthTarget: {},
        monthPercent: {},
        totalActual: 0,
        totalTarget: 0,
      }
      for (const m of months) {
        const allDetails = user.kpiDetails[m] ?? []
        const details =
          selectedKpi === 'all'
            ? allDetails
            : allDetails.filter((d) => d.key === selectedKpi)
        let sumActual = 0
        let sumTarget = 0
        const percents: number[] = []
        for (const d of details) {
          sumActual += d.actual
          if (d.target != null) sumTarget += d.target
          if (d.percent != null) percents.push(d.percent)
        }
        agg.monthActual[m] = sumActual
        agg.monthTarget[m] = sumTarget
        agg.monthPercent[m] =
          percents.length > 0
            ? percents.reduce((a, b) => a + b, 0) / percents.length
            : null
        agg.totalActual += sumActual
        agg.totalTarget += sumTarget
      }
      map.set(user.user_id, agg)
    }
    return map
  }, [results, months, selectedKpi])

  const seasonalityData = useMemo(() => {
    const enabledConfigs = configs.filter((c) => !disabledKpis.has(c.key))

    return enabledConfigs.map((config) => {
      // Group raw data by month → user → summed actual
      const monthUserActuals = new Map<string, Map<number, number>>()

      for (const row of loxoData) {
        if (row.kpi !== config.key) continue
        const month = months.find((m) => row.period.startsWith(m))
        if (!month) continue
        if (!monthUserActuals.has(month)) monthUserActuals.set(month, new Map())
        const userMap = monthUserActuals.get(month)!
        userMap.set(row.user_id, (userMap.get(row.user_id) ?? 0) + row.value)
      }

      // Overall avg per active user-month (actual > 0)
      let totalActuals = 0
      let totalActiveUserMonths = 0
      const monthStats = new Map<string, { sum: number; activeUsers: number }>()

      for (const m of months) {
        const userMap = monthUserActuals.get(m)
        let monthSum = 0
        let activeUsers = 0
        if (userMap) {
          userMap.forEach((actual) => {
            if (actual > 0) {
              monthSum += actual
              activeUsers++
            }
          })
        }
        monthStats.set(m, { sum: monthSum, activeUsers })
        totalActuals += monthSum
        totalActiveUserMonths += activeUsers
      }

      const overallAvg =
        totalActiveUserMonths > 0 ? totalActuals / totalActiveUserMonths : 0

      const monthSeasonality: Record<
        string,
        { index: number; activeUsers: number } | null
      > = {}
      for (const m of months) {
        const stats = monthStats.get(m)!
        if (stats.activeUsers === 0 || overallAvg === 0) {
          monthSeasonality[m] = null
        } else {
          monthSeasonality[m] = {
            index: (stats.sum / stats.activeUsers / overallAvg) * 100,
            activeUsers: stats.activeUsers,
          }
        }
      }

      return {
        key: config.key,
        name: config.name,
        overallAvg,
        months: monthSeasonality,
      }
    })
  }, [loxoData, configs, months, disabledKpis])

  const updateOverride = useCallback(
    (
      key: string,
      field: 'month_target' | 'month_percent_values' | 'month_score_values',
      value: number | null | number[]
    ) => {
      setOverrides((prev) => ({
        ...prev,
        [key]: { ...prev[key], [field]: value },
      }))
    },
    []
  )

  const toggleKpi = useCallback((key: string) => {
    setDisabledKpis((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const toggleUser = useCallback((key: string) => {
    setExpandedUsers((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const formatNum = (n: number | null) =>
    n == null ? '—' : n.toLocaleString(undefined, { maximumFractionDigits: 2 })

  const formatScore = (score: number, max: number) => (
    <>
      {formatNum(score)}
      <Text span size="xs" c="dimmed">
        {' '}
        / {formatNum(max)}
      </Text>
    </>
  )

  const getCellValue = (userId: number, month: string | null) => {
    const info = userMaxScores.get(userId)
    if (!info) return '—'

    // Inactive month → dash
    if (month && !info.monthActive[month]) return '—'
    if (!month && !months.some((m) => info.monthActive[m])) return '—'

    const maxScore = month ? (info.monthMax[month] ?? 0) : info.totalMax

    // Compute effective score
    let score: number
    const user = results.find((r) => r.user_id === userId)
    if (!user) return '—'

    if (selectedKpi !== 'all') {
      if (month) {
        const detail = (user.kpiDetails[month] ?? []).find(
          (d) => d.key === selectedKpi
        )
        score = detail?.score ?? 0
      } else {
        score = months.reduce((sum, m) => {
          if (!info.monthActive[m]) return sum
          const detail = (user.kpiDetails[m] ?? []).find(
            (d) => d.key === selectedKpi
          )
          return sum + (detail?.score ?? 0)
        }, 0)
      }
    } else {
      if (month) {
        score = user.monthPoints[month] ?? 0
      } else {
        score = months.reduce((sum, m) => {
          if (!info.monthActive[m]) return sum
          return sum + (user.monthPoints[m] ?? 0)
        }, 0)
      }
    }

    const agg = userAggregates.get(userId)
    switch (viewMode) {
      case 'score':
        return formatScore(score, maxScore)
      case 'rating':
        return formatNum(maxScore > 0 ? (10 * score) / maxScore : 0)
      case 'actual':
        return formatNum(
          month ? (agg?.monthActual[month] ?? 0) : (agg?.totalActual ?? 0)
        )
      case 'target':
        return formatNum(
          month ? (agg?.monthTarget[month] ?? 0) : (agg?.totalTarget ?? 0)
        )
      case 'percent': {
        if (month) {
          const pct = agg?.monthPercent[month]
          return pct != null ? `${(pct * 100).toFixed(1)}%` : '—'
        }
        const totalA = agg?.totalActual ?? 0
        const totalT = agg?.totalTarget ?? 0
        return totalT > 0 ? `${((totalA / totalT) * 100).toFixed(1)}%` : '—'
      }
    }
  }

  const getTotalCellValue = (score: number, month: string | null) => {
    switch (viewMode) {
      case 'score':
        return formatNum(score)
      case 'rating': {
        let sumRating = 0
        let count = 0
        for (const u of results) {
          const info = userMaxScores.get(u.user_id)
          if (!info) continue
          if (month && !info.monthActive[month]) continue
          if (!month && !months.some((m) => info.monthActive[m])) continue

          const max = month ? (info.monthMax[month] ?? 0) : info.totalMax
          let s: number
          if (selectedKpi !== 'all') {
            if (month) {
              const detail = (u.kpiDetails[month] ?? []).find(
                (d) => d.key === selectedKpi
              )
              s = detail?.score ?? 0
            } else {
              s = months.reduce((sum, m) => {
                if (!info.monthActive[m]) return sum
                const detail = (u.kpiDetails[m] ?? []).find(
                  (d) => d.key === selectedKpi
                )
                return sum + (detail?.score ?? 0)
              }, 0)
            }
          } else {
            if (month) {
              s = u.monthPoints[month] ?? 0
            } else {
              s = months.reduce((sum, m) => {
                if (!info.monthActive[m]) return sum
                return sum + (u.monthPoints[m] ?? 0)
              }, 0)
            }
          }
          sumRating += max > 0 ? (10 * s) / max : 0
          count++
        }
        return count > 0 ? formatNum(sumRating / count) : '—'
      }
      case 'actual': {
        let sum = 0
        for (const u of results) {
          const agg = userAggregates.get(u.user_id)
          sum += month
            ? (agg?.monthActual[month] ?? 0)
            : (agg?.totalActual ?? 0)
        }
        return formatNum(sum)
      }
      case 'target': {
        let sum = 0
        for (const u of results) {
          const agg = userAggregates.get(u.user_id)
          sum += month
            ? (agg?.monthTarget[month] ?? 0)
            : (agg?.totalTarget ?? 0)
        }
        return formatNum(sum)
      }
      case 'percent': {
        let totalA = 0
        let totalT = 0
        for (const u of results) {
          const agg = userAggregates.get(u.user_id)
          totalA += month
            ? (agg?.monthActual[month] ?? 0)
            : (agg?.totalActual ?? 0)
          totalT += month
            ? (agg?.monthTarget[month] ?? 0)
            : (agg?.totalTarget ?? 0)
        }
        return totalT > 0 ? `${((totalA / totalT) * 100).toFixed(1)}%` : '—'
      }
    }
  }

  return (
    <Stack gap="md">
      {/* Config Editor */}
      <div className={classes.controlsBar}>
        <Group justify="space-between" w="100%">
          <Group gap="sm">
            <UnstyledButton onClick={() => setConfigOpen((v) => !v)}>
              <Group gap={4}>
                {configOpen ? (
                  <IconChevronDown size={16} />
                ) : (
                  <IconChevronRight size={16} />
                )}
                <Text fw={600} size="sm">
                  Scoring Configuration
                </Text>
              </Group>
            </UnstyledButton>
            <Select
              size="xs"
              w={180}
              value={targetMode}
              onChange={(v) => setTargetMode((v as TargetMode) ?? 'static')}
              data={[
                { value: 'static', label: 'Static Targets' },
                { value: 'blended', label: 'Blended Mean' },
                { value: 'percentile', label: 'Percentile' },
                { value: 'regression', label: 'Regression to Mean' },
              ]}
              allowDeselect={false}
            />
          </Group>
          {Object.keys(overrides).length > 0 && (
            <Group gap="xs">
              <Button
                variant="subtle"
                size="xs"
                onClick={() => setOverrides({})}
              >
                Reset to Defaults
              </Button>
              <Button
                variant="subtle"
                size="xs"
                loading={saveMutation.isPending}
                onClick={() => {
                  const configsToSave = configs
                    .filter((c) => overrides[c.key])
                    .map((c) => {
                      const ov = overrides[c.key]
                      return {
                        metric_id: c.metric_id,
                        month_target:
                          ov.month_target !== undefined
                            ? ov.month_target
                            : c.month_target,
                        month_percent_values:
                          ov.month_percent_values ?? c.month_percent_values,
                        month_score_values:
                          ov.month_score_values ?? c.month_score_values,
                      }
                    })
                  saveMutation.mutate(configsToSave, {
                    onSuccess: () => setOverrides({}),
                  })
                }}
              >
                Save
              </Button>
            </Group>
          )}
        </Group>
      </div>

      {targetMode !== 'static' && (
        <Group gap="md" px="sm" align="flex-end">
          <Group gap={4} align="center">
            <Text size="xs" c="dimmed">
              {targetMode === 'blended' &&
                'Blends personal avg with trimmed group avg, adjusted for seasonality.'}
              {targetMode === 'percentile' &&
                'Targets users above their current percentile — harder for top, easier for bottom.'}
              {targetMode === 'regression' &&
                'Pulls targets toward group median — compresses the spread.'}
            </Text>
            <Tooltip
              multiline
              w={320}
              withArrow
              position="bottom"
              label={
                targetMode === 'blended'
                  ? 'For each KPI, computes a trimmed mean of all users (removing outliers at top/bottom by Trim %). ' +
                    "Each user's target is a weighted blend: \u03b1 \u00d7 their personal average + (1\u2013\u03b1) \u00d7 trimmed group average. " +
                    "The result is then scaled by the month's seasonality index so targets are higher in busy months and lower in slow ones. " +
                    'Higher \u03b1 = more personalized; lower \u03b1 = more uniform across the team.'
                  : targetMode === 'percentile'
                    ? 'Ranks all users by their average performance for each KPI. ' +
                      "Each user's target is set at a percentile slightly above their current rank — the gap is controlled by Max Stretch. " +
                      'Top performers get targets near the ceiling (small room to stretch), while lower performers get more achievable targets that still push them upward. ' +
                      'Targets are then adjusted for monthly seasonality. This naturally creates a fairness gradient across skill levels.'
                    : "Computes the group median for each KPI and blends it with each user's personal average using the Decay factor. " +
                      "The target is whichever is higher: the user's own average or the regressed value. " +
                      'This means high performers keep their current bar, while low performers get pulled up toward the median. ' +
                      'Higher Decay = stronger pull toward the group center. Targets are seasonality-adjusted per month.'
              }
            >
              <IconInfoCircle
                size={14}
                style={{ color: 'var(--mantine-color-dimmed)', cursor: 'help' }}
              />
            </Tooltip>
          </Group>
          {targetMode === 'blended' && (
            <>
              <NumberInput
                size="xs"
                label="Blend α"
                value={dynamicParams.blendAlpha}
                onChange={(v) =>
                  setDynamicParams((p) => ({
                    ...p,
                    blendAlpha: Number(v) || 0,
                  }))
                }
                min={0}
                max={1}
                step={0.05}
                decimalScale={2}
                style={{ width: 90 }}
              />
              <NumberInput
                size="xs"
                label="Trim %"
                value={dynamicParams.trimPercent}
                onChange={(v) =>
                  setDynamicParams((p) => ({
                    ...p,
                    trimPercent: Number(v) || 0,
                  }))
                }
                min={0}
                max={50}
                step={5}
                style={{ width: 90 }}
              />
            </>
          )}
          {targetMode === 'percentile' && (
            <NumberInput
              size="xs"
              label="Max Stretch"
              value={dynamicParams.maxStretch}
              onChange={(v) =>
                setDynamicParams((p) => ({ ...p, maxStretch: Number(v) || 0 }))
              }
              min={0}
              max={1}
              step={0.05}
              decimalScale={2}
              style={{ width: 110 }}
            />
          )}
          {targetMode === 'regression' && (
            <NumberInput
              size="xs"
              label="Decay"
              value={dynamicParams.regressionDecay}
              onChange={(v) =>
                setDynamicParams((p) => ({
                  ...p,
                  regressionDecay: Number(v) || 0,
                }))
              }
              min={0}
              max={1}
              step={0.05}
              decimalScale={2}
              style={{ width: 90 }}
            />
          )}
        </Group>
      )}

      {configOpen && (
        <Table.ScrollContainer minWidth={700}>
          <Table
            classNames={{
              table: classes.table,
              th: classes.th,
              td: classes.td,
              tr: classes.headerRow,
            }}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 40 }}>
                  <Checkbox
                    size="xs"
                    checked={disabledKpis.size === 0}
                    indeterminate={
                      disabledKpis.size > 0 &&
                      disabledKpis.size < configs.length
                    }
                    onChange={() => {
                      if (disabledKpis.size === 0) {
                        setDisabledKpis(new Set(configs.map((c) => c.key)))
                      } else {
                        setDisabledKpis(new Set())
                      }
                    }}
                  />
                </Table.Th>
                <Table.Th style={{ minWidth: 140, textAlign: 'left' }}>
                  KPI
                </Table.Th>
                <Table.Th style={{ minWidth: 100 }}>Target</Table.Th>
                <Table.Th style={{ minWidth: 200 }}>
                  Percent Thresholds
                </Table.Th>
                <Table.Th style={{ minWidth: 200 }}>Score Values</Table.Th>
                <Table.Th style={{ width: 60 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {configs.map((config) => {
                const ov = overrides[config.key]
                const target =
                  ov?.month_target !== undefined
                    ? ov.month_target
                    : config.month_target
                const pctValues =
                  ov?.month_percent_values ?? config.month_percent_values
                const scoreValues =
                  ov?.month_score_values ?? config.month_score_values

                return (
                  <Table.Tr key={config.metric_id}>
                    <Table.Td>
                      <Checkbox
                        size="xs"
                        checked={!disabledKpis.has(config.key)}
                        onChange={() => toggleKpi(config.key)}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {config.name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {config.key}
                        {config.is_percent_metric ? ' (%)' : ''}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {config.is_percent_metric ? (
                        <Text size="sm" c="dimmed">
                          N/A
                        </Text>
                      ) : (
                        <NumberInput
                          size="xs"
                          value={target ?? ''}
                          onChange={(v) =>
                            updateOverride(
                              config.key,
                              'month_target',
                              v === '' ? null : Number(v)
                            )
                          }
                          min={0}
                          style={{ width: 80 }}
                          disabled={targetMode !== 'static'}
                        />
                      )}
                    </Table.Td>
                    <Table.Td>
                      <ArrayEditor
                        values={pctValues}
                        onChange={(v) =>
                          updateOverride(config.key, 'month_percent_values', v)
                        }
                      />
                    </Table.Td>
                    <Table.Td>
                      <ArrayEditor
                        values={scoreValues}
                        onChange={(v) =>
                          updateOverride(config.key, 'month_score_values', v)
                        }
                      />
                    </Table.Td>
                    <Table.Td>
                      <Group gap={2} wrap="nowrap">
                        <ActionIcon
                          variant="subtle"
                          size="xs"
                          onClick={() =>
                            setCopiedConfig({
                              month_target: target,
                              month_percent_values: [...pctValues],
                              month_score_values: [...scoreValues],
                            })
                          }
                          title="Copy config"
                        >
                          <IconCopy size={14} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          size="xs"
                          disabled={copiedConfig == null}
                          onClick={() => {
                            if (!copiedConfig) return
                            setOverrides((prev) => ({
                              ...prev,
                              [config.key]: {
                                month_target: copiedConfig.month_target,
                                month_percent_values: [
                                  ...copiedConfig.month_percent_values,
                                ],
                                month_score_values: [
                                  ...copiedConfig.month_score_values,
                                ],
                              },
                            }))
                          }}
                          title="Paste config"
                        >
                          <IconClipboard size={14} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      {/* Seasonality */}
      <div className={classes.controlsBar}>
        <UnstyledButton onClick={() => setSeasonalityOpen((v) => !v)}>
          <Group gap={4}>
            {seasonalityOpen ? (
              <IconChevronDown size={16} />
            ) : (
              <IconChevronRight size={16} />
            )}
            <Text fw={600} size="sm">
              Seasonality
            </Text>
          </Group>
        </UnstyledButton>
      </div>

      {seasonalityOpen && seasonalityData.length > 0 && (
        <Table.ScrollContainer minWidth={600}>
          <Table
            classNames={{
              table: clsx(classes.table, classes.stickyColumns),
              th: classes.th,
              td: classes.td,
              tr: classes.headerRow,
            }}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ minWidth: isDesktop ? 160 : 120 }}>
                  KPI
                </Table.Th>
                <Table.Th>Avg/User</Table.Th>
                {months.map((m) => (
                  <Table.Th key={m}>{dayjs(m).format('MMM YY')}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {seasonalityData.map((kpi) => (
                <Table.Tr key={kpi.key}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {kpi.name}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{formatNum(kpi.overallAvg)}</Text>
                  </Table.Td>
                  {months.map((m) => {
                    const s = kpi.months[m]
                    const bg =
                      s == null
                        ? undefined
                        : s.index > 105
                          ? 'var(--mantine-color-green-light)'
                          : s.index < 95
                            ? 'var(--mantine-color-red-light)'
                            : undefined
                    return (
                      <Table.Td
                        key={m}
                        style={bg ? { backgroundColor: bg } : undefined}
                      >
                        <Text size="sm">
                          {s ? `${s.index.toFixed(0)}%` : '—'}
                        </Text>
                      </Table.Td>
                    )
                  })}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      {/* Results */}
      <Group gap="sm" align="center">
        <Text fw={500} size="lg">
          Scoring Results
        </Text>
        <Select
          size="xs"
          w={200}
          value={selectedKpi}
          onChange={(v) => {
            const next = v ?? 'all'
            setSelectedKpi(next)
            if (next === 'all' && !['score', 'rating'].includes(viewMode)) {
              setViewMode('score')
            }
          }}
          data={kpiOptions}
          allowDeselect={false}
        />
        <Select
          size="xs"
          w={140}
          value={viewMode}
          onChange={(v) => setViewMode((v as typeof viewMode) ?? 'score')}
          data={viewModeOptions}
          allowDeselect={false}
        />
      </Group>

      {results.length === 0 ? (
        <Text className={classes.empty}>
          No data available for the selected date range.
        </Text>
      ) : (
        <Table.ScrollContainer minWidth={600}>
          <Table
            classNames={{
              table: clsx(classes.table, classes.stickyColumns),
              th: classes.th,
              td: classes.td,
              tr: classes.headerRow,
            }}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ minWidth: isDesktop ? 160 : 120 }}>
                  Name
                </Table.Th>
                <Table.Th>Total</Table.Th>
                {months.map((m) => (
                  <Table.Th key={m}>{dayjs(m).format('MMM YY')}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr className={classes.totalsRow}>
                <Table.Td>Total</Table.Td>
                <Table.Td style={{ fontWeight: 700 }}>
                  {getTotalCellValue(totals.grandTotal, null)}
                </Table.Td>
                {months.map((m) => (
                  <Table.Td key={m}>
                    {getTotalCellValue(totals.monthTotals[m] ?? 0, m)}
                  </Table.Td>
                ))}
              </Table.Tr>
              {results.map((user) => {
                const userKey = `${user.user_id}`
                const isExpanded = expandedUsers.has(userKey)
                return (
                  <>
                    <Table.Tr key={user.user_id}>
                      <Table.Td>
                        <UnstyledButton onClick={() => toggleUser(userKey)}>
                          <Group gap={4}>
                            {isExpanded ? (
                              <IconChevronDown size={14} />
                            ) : (
                              <IconChevronRight size={14} />
                            )}
                            <Text size="sm">{user.name}</Text>
                          </Group>
                        </UnstyledButton>
                      </Table.Td>
                      <Table.Td style={{ fontWeight: 600 }}>
                        {getCellValue(user.user_id, null)}
                      </Table.Td>
                      {months.map((m) => (
                        <Table.Td key={m}>
                          {getCellValue(user.user_id, m)}
                        </Table.Td>
                      ))}
                    </Table.Tr>
                    {isExpanded &&
                      (() => {
                        // Get sorted KPI list from any month's details
                        const firstMonth = months.find(
                          (m) => (user.kpiDetails[m] ?? []).length > 0
                        )
                        if (!firstMonth) return null
                        const kpiKeys = (user.kpiDetails[firstMonth] ?? []).map(
                          (d) => d.key
                        )

                        return kpiKeys.flatMap((kpiKey) => {
                          // Gather per-month data for this KPI
                          const perMonth = months.map((m) => {
                            const details = user.kpiDetails[m] ?? []
                            return details.find((d) => d.key === kpiKey) ?? null
                          })
                          const kpiName =
                            perMonth.find((d) => d != null)?.name ?? kpiKey
                          const configured =
                            perMonth.find((d) => d != null)?.configured ?? false

                          const label = configured
                            ? kpiName
                            : `${kpiName} (unconfigured)`

                          const sumActual = perMonth.reduce(
                            (s, d) => s + (d?.actual ?? 0),
                            0
                          )
                          const sumScore = perMonth.reduce(
                            (s, d) => s + (d?.score ?? 0),
                            0
                          )
                          const sumTarget = perMonth.reduce(
                            (s, d) => s + (d?.target ?? 0),
                            0
                          )

                          const rows: [
                            string,
                            string | null,
                            (d: KpiDetail | null, monthIdx: number) => string,
                          ][] = [
                            [
                              'Actual',
                              formatNum(sumActual),
                              (d) => formatNum(d?.actual ?? 0),
                            ],
                            [
                              'Target',
                              formatNum(sumTarget || null),
                              (d) => formatNum(d?.target ?? null),
                            ],
                            [
                              'Percent',
                              '—',
                              (d) =>
                                d?.percent != null
                                  ? `${(d.percent * 100).toFixed(0)}%`
                                  : '—',
                            ],
                            [
                              'Score',
                              formatNum(sumScore),
                              (d) => formatNum(d?.score ?? null),
                            ],
                          ]

                          return rows.map(([metric, total, cellFn]) => (
                            <Table.Tr
                              key={`${user.user_id}-${kpiKey}-${metric}`}
                              className={classes.kpiRow}
                            >
                              <Table.Td>
                                <Text size="xs">
                                  {label}: {metric}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="xs">{total}</Text>
                              </Table.Td>
                              {months.map((m, mi) => (
                                <Table.Td key={m}>
                                  <Text size="xs">
                                    {cellFn(perMonth[mi], mi)}
                                  </Text>
                                </Table.Td>
                              ))}
                            </Table.Tr>
                          ))
                        })
                      })()}
                  </>
                )
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  )
}

function ArrayEditor({
  values,
  onChange,
}: {
  values: number[]
  onChange: (values: number[]) => void
}) {
  return (
    <Group gap={4} wrap="wrap">
      {values.map((v, i) => (
        <NumberInput
          key={i}
          size="xs"
          value={v}
          onChange={(newVal) => {
            const next = [...values]
            next[i] = Number(newVal) || 0
            onChange(next)
          }}
          style={{ width: 60 }}
          step={0.1}
          decimalScale={2}
        />
      ))}
      <Button
        variant="subtle"
        size="compact-xs"
        onClick={() => onChange([...values, 0])}
      >
        +
      </Button>
      {values.length > 0 && (
        <Button
          variant="subtle"
          size="compact-xs"
          c="red"
          onClick={() => onChange(values.slice(0, -1))}
        >
          −
        </Button>
      )}
    </Group>
  )
}
